import React, { useState } from 'react';
import { Button } from './ui/Button';
import api from '../lib/api';

interface UpdateTestModalProps {
  testId: string;
  currentStatus?: string;
  onClose: () => void;
  onSaved: () => void;
}

const PLATFORM_OPTIONS = ['Facebook', 'TikTok', 'YouTube', 'Instagram', 'Google', 'Other'];

const UNTESTED_REASONS = [
  { value: 'no_budget', label: 'No budget allocated' },
  { value: 'pending_approval', label: 'Waiting for approval' },
  { value: 'wrong_format', label: "Format doesn't match platform" },
  { value: 'duplicate', label: 'Duplicate creative' },
  { value: 'low_priority', label: 'Low priority' },
  { value: 'awaiting_revision', label: 'Needs revision' },
  { value: 'platform_unavailable', label: 'Platform unavailable' },
  { value: 'buyer_unavailable', label: 'No buyer assigned' },
];

export function UpdateTestModal({ testId, currentStatus, onClose, onSaved }: UpdateTestModalProps) {
  const [status, setStatus] = useState<string>(currentStatus || 'untested');
  const [saving, setSaving] = useState(false);

  // Tested fields
  const [dateTested, setDateTested] = useState('');
  const [platform, setPlatform] = useState('');
  const [ctr, setCtr] = useState('');
  const [hookRate, setHookRate] = useState('');
  const [holdRate, setHoldRate] = useState('');
  const [roi, setRoi] = useState('');
  const [feedback, setFeedback] = useState('');
  const [scalingRec, setScalingRec] = useState('');

  // Untested fields
  const [reason, setReason] = useState('');
  const [timeline, setTimeline] = useState('');
  const [commStatus, setCommStatus] = useState('');

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = { status };
      if (status === 'tested') {
        if (dateTested) payload.date_tested = dateTested;
        if (platform) payload.platform = platform;
        if (ctr) payload.ctr = parseFloat(ctr);
        if (hookRate) payload.hook_rate = parseFloat(hookRate);
        if (holdRate) payload.hold_rate = parseFloat(holdRate);
        if (roi) payload.roi = parseFloat(roi);
        if (feedback) payload.feedback = feedback;
        if (scalingRec) payload.scaling_recommendation = scalingRec;
      } else if (status === 'untested') {
        if (reason) payload.untested_reason = reason;
        if (timeline) payload.timeline = timeline;
        if (commStatus) payload.communication_status = commStatus;
      } else if (status === 'scheduled') {
        if (timeline) payload.timeline = timeline;
      }
      await api.put(`/creative-tests/${testId}`, payload);
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border">
          <h2 className="text-lg font-semibold">Update Test Status</h2>
        </div>
        <div className="p-5 space-y-4">
          {/* Status selector */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-2">Status</label>
            <div className="flex gap-2">
              {[
                { value: 'tested', label: 'Launched' },
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'untested', label: 'Not Launched' },
              ].map(({ value: s, label }) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-4 py-1.5 text-sm rounded-full border font-medium transition-colors ${
                    status === s
                      ? s === 'tested' ? 'bg-green-600 text-white border-green-600'
                        : s === 'scheduled' ? 'bg-yellow-500 text-white border-yellow-500'
                        : 'bg-red-500 text-white border-red-500'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {status === 'tested' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Date Tested</label>
                  <input type="date" value={dateTested} onChange={e => setDateTested(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Platform</label>
                  <select value={platform} onChange={e => setPlatform(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary">
                    <option value="">Select...</option>
                    {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">CTR %</label>
                  <input type="number" step="0.01" value={ctr} onChange={e => setCtr(e.target.value)} placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Hook Rate %</label>
                  <input type="number" step="0.01" value={hookRate} onChange={e => setHookRate(e.target.value)} placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Hold Rate %</label>
                  <input type="number" step="0.01" value={holdRate} onChange={e => setHoldRate(e.target.value)} placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">ROI</label>
                  <input type="number" step="0.01" value={roi} onChange={e => setRoi(e.target.value)} placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Feedback</label>
                <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary resize-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Scaling Recommendation</label>
                <textarea value={scalingRec} onChange={e => setScalingRec(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary resize-none" />
              </div>
            </>
          )}

          {status === 'untested' && (
            <>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Reason</label>
                <select value={reason} onChange={e => setReason(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary">
                  <option value="">Select reason...</option>
                  {UNTESTED_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Timeline</label>
                <input type="date" value={timeline} onChange={e => setTimeline(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Communication Status</label>
                <input type="text" value={commStatus} onChange={e => setCommStatus(e.target.value)} placeholder="e.g. Waiting on buyer response"
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
              </div>
            </>
          )}

          {status === 'scheduled' && (
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1">Timeline <span className="text-red-500">*</span></label>
              <input type="date" value={timeline} onChange={e => setTimeline(e.target.value)} required
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary" />
            </div>
          )}
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || (status === 'scheduled' && !timeline)}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
